import {
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';

import {
    add,
    deleteRecord,
    fetch,
    update,
} from '../services/PaymentIntentsService';
import {paymentIntentsKeys} from './usePaymentIntentsQuery';
import {PaymentIntent} from '../types';
import {
    notifyError,
    notifySuccess,
} from '../../../components/ServiceNotifications';

/**
 * Single-record hook for payment-intents.
 *
 * ═══ MONEY PATH ═══
 *
 * This module creates, updates and cancels PaymentIntents. FNP-918 changed
 * three things here and nothing else: the CACHE KEYS, the payload construction
 * (moved into the service as an allowlist — see PaymentIntentsService.ts), and
 * the fact that the returned mutators now RESOLVE. No confirm/capture/cancel
 * semantics, no idempotency key, and no `clientSecret` handling is touched.
 *
 * ⚠️ THE KEYS WERE STALE AND WOULD HAVE SILENTLY STOPPED WORKING.
 *
 * Every mutation below invalidated the bare string key `['paymentIntents']`.
 * The list moved to the structured `paymentIntentsKeys.all` =
 * `['payment-intents']` — a DIFFERENT key, note the hyphen. Left as it was,
 * creating a payment intent would have shown a success notification and left
 * the table showing the old rows until a manual reload. Both the list and the
 * detail key are taken from the same factory now, so they cannot drift again.
 */
export const usePaymentIntent = (paymentIntentId?: string) => {
    const queryClient = useQueryClient();

    const paymentIntentQuery = useQuery({
        queryKey: paymentIntentsKeys.detail(paymentIntentId ?? ''),
        queryFn: () => fetch(paymentIntentId!),
        enabled: !!paymentIntentId,
    });

    // ⚠️ MONEY PATH — this creates a payment intent.
    //
    // The denylist that used to live here (`{...values}` then
    // `delete payload.created`) is gone: `add` in the service now builds the
    // input by ALLOWLIST from `CreatePaymentIntentInput`, so the values object
    // is passed through untouched and the service decides what is sent. That
    // removes the copy-then-delete dance entirely rather than moving it.
    const createMutation = useMutation({
        mutationFn: (values: PaymentIntent) => add(values),
        onSuccess: () => {
            notifySuccess('Payment intent created');
            queryClient.invalidateQueries({queryKey: paymentIntentsKeys.all});
        },
        onError: notifyError,
    });

    const updateMutation = useMutation({
        mutationFn: (values: PaymentIntent) => {
            if (!paymentIntentId) {
                throw new Error('paymentIntentId is required parameter');
            }
            return update(paymentIntentId, values);
        },
        onSuccess: () => {
            notifySuccess('Payment intent updated');
            // `all` covers both the lists and this record's detail entry, so
            // the table and the profile agree after an edit.
            queryClient.invalidateQueries({queryKey: paymentIntentsKeys.all});
        },
        onError: notifyError,
    });

    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!paymentIntentId) {
                throw new Error('paymentIntentId is required parameter');
            }
            return deleteRecord(paymentIntentId);
        },
        onSuccess: () => {
            notifySuccess('Payment intent deleted');
            queryClient.invalidateQueries({queryKey: paymentIntentsKeys.all});
        },
        onError: notifyError,
    });

    return {
        record: paymentIntentQuery.data,
        loading: paymentIntentQuery.isLoading,
        isError: paymentIntentQuery.isError,
        error: paymentIntentQuery.error,
        fetchRecord: paymentIntentQuery.refetch,
        /**
         * ⚠️ `mutateAsync`, NOT `mutate`.
         *
         * `mutate` returns `void` and swallows rejections. Every caller of these
         * is a drawer or form that does `await handleSubmit(values)` before
         * closing — with `mutate` that await resolved INSTANTLY, so the drawer
         * shut before the request finished, `isSubmitting` never engaged, and
         * the submit button stayed clickable for the whole in-flight window.
         * On a module that creates payment intents, a double-click there is a
         * second charge, not a duplicate row.
         *
         * `mutateAsync` rejects on failure, so callers MUST handle the
         * rejection — `onError` above still fires and notifies either way, and
         * the drawer keeps itself open by not catching. See drawers/FormDrawer.
         */
        createRecord: createMutation.mutateAsync,
        updateRecord: updateMutation.mutateAsync,
        deleteRecord: deleteMutation.mutateAsync,
        createMutation,
        updateMutation,
        deleteMutation,
    };
};
